import { Pattern } from '../../types/pattern';
import { STITCH_CONSTANTS } from '../../utils/stitchConstants';
import { VARIANT_CONFIGS, VARIANT_TO_GROUP } from '../../stitches';
import { drawStitch } from './drawStitch';

const { PADDING: LEGEND_PADDING, STITCH_SIZE, STITCH_TEXT_GAP, ITEMS_PER_ROW, ROW_GAP,
        TEXT_COLOR, STITCH_COLOR, MAX_TEXT_WIDTH, LINE_HEIGHT } = STITCH_CONSTANTS.LEGEND;

interface StitchLegendTranslations {
  stitchTypes: { [key: string]: string | undefined };
  legend: { signature: string };
}

interface StitchLegendProps {
  pattern: Pattern;
  ctx: CanvasRenderingContext2D;
  startY: number;
  canvasWidth: number;
  translations: StitchLegendTranslations;
  language: string;
}

// Word-wrap text into lines
const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const width = ctx.measureText(currentLine + ' ' + words[i]).width;
    if (width < maxWidth) {
      currentLine += ' ' + words[i];
    } else {
      lines.push(currentLine);
      currentLine = words[i];
    }
  }
  lines.push(currentLine);

  return lines;
};

export const measureStitchLegendHeight = (pattern: Pattern): number => {
  const usedStitchTypes = new Set<string>();
  pattern.stitches.forEach(stitch => {
    const group = VARIANT_TO_GROUP[stitch.type];
    if (!group || group.config.category !== 'marker') {
      usedStitchTypes.add(stitch.type);
    }
  });
  const rowCount = Math.ceil(usedStitchTypes.size / ITEMS_PER_ROW);
  return rowCount * (STITCH_SIZE + ROW_GAP) + LEGEND_PADDING * 2;
};

export const drawStitchLegend = ({ pattern, ctx, startY, canvasWidth, translations, language }: StitchLegendProps) => {
  // Collect unique stitch types, excluding markers
  const usedStitchTypes = new Set<string>();
  pattern.stitches.forEach(stitch => {
    const group = VARIANT_TO_GROUP[stitch.type];
    if (!group || group.config.category !== 'marker') {
      usedStitchTypes.add(stitch.type);
    }
  });

  // Convert to array for drawing
  const stitchesToDraw = Array.from(usedStitchTypes);

  // Calculate legend dimensions
  const rowCount = Math.ceil(stitchesToDraw.length / ITEMS_PER_ROW);
  const legendHeight = rowCount * (STITCH_SIZE + ROW_GAP) + LEGEND_PADDING * 2;

  // Calculate item width and total legend width
  const itemWidth = STITCH_SIZE + STITCH_TEXT_GAP + MAX_TEXT_WIDTH;
  const totalLegendWidth = itemWidth * ITEMS_PER_ROW;
  
  // Calculate left offset for centering
  const leftOffset = (canvasWidth - totalLegendWidth) / 2;

  // Set text styles
  ctx.font = '14px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = TEXT_COLOR;

  // Draw each stitch with its name
  stitchesToDraw.forEach((stitchType, index) => {
    const row = Math.floor(index / ITEMS_PER_ROW);
    const col = index % ITEMS_PER_ROW;

    const x = leftOffset + col * itemWidth;
    const y = startY + LEGEND_PADDING + row * (STITCH_SIZE + ROW_GAP);

    // Draw stitch
    ctx.save();
    ctx.translate(x + STITCH_SIZE / 2, y + STITCH_SIZE / 2);
    
    drawStitch({
      ctx,
      stitch: { type: stitchType },
      color: STITCH_COLOR,
      stitchScale: 1.5
    });

    ctx.restore();

    // Use the group's default variant name for legend display
    // So "chain-horizontal" shows as "Chain Stitch" (same as "chain")
    const group = VARIANT_TO_GROUP[stitchType];
    const defaultVariant = group ? VARIANT_CONFIGS[group.defaultVariantId] : null;
    const variantCfg = defaultVariant || VARIANT_CONFIGS[stitchType];
    const stitchName = variantCfg
      ? (variantCfg.names[language] || variantCfg.names['en'] || stitchType)
      : (translations.stitchTypes[stitchType] || stitchType);
    
    // Word-wrap the name
    const lines = wrapText(ctx, stitchName, MAX_TEXT_WIDTH);
    
    // Draw each text line
    lines.forEach((line, lineIndex) => {
      // Center single-line text vertically
      const yOffset = lines.length === 1 
        ? (STITCH_SIZE - LINE_HEIGHT) / 2  // Vertically centered for single line
        : lineIndex * LINE_HEIGHT;         // Normal positioning for multi-line text

      ctx.fillText(
        line,
        x + STITCH_SIZE + STITCH_TEXT_GAP,
        y + yOffset
      );
    });
  });

  // Add signature in bottom-left corner
  ctx.save();
  ctx.font = '12px Arial';
  ctx.fillStyle = 'rgba(74, 74, 74, 0.7)';
  const signatureText = translations.legend.signature;
  ctx.fillText(
    signatureText,
    LEGEND_PADDING,
    startY + legendHeight - LEGEND_PADDING
  );
  ctx.restore();
  
  return legendHeight;
}; 